import { useEffect, useMemo, useState } from 'react';
import { detectExtensionBrowserTargetFromNavigator } from '@flow-doc/shared';
import { ApiError, apiFetch } from '../utils/api';
import { isExtensionUpdateAvailable, } from '../utils/extensionVersion';
const READY_EVENT = 'cricket-studio-extension-ready';
const HEARTBEAT_EVENT = 'cricket-studio-extension-heartbeat';
function getDetail(event) {
    const detail = event.detail;
    return detail && typeof detail === 'object'
        ? detail
        : {};
}
export function useExtensionVersionStatus(enabled = true) {
    const [installedVersion, setInstalledVersion] = useState(undefined);
    const [installedBuildSha, setInstalledBuildSha] = useState(undefined);
    const [lastHeartbeatAt, setLastHeartbeatAt] = useState(undefined);
    const [latestArtifact, setLatestArtifact] = useState(undefined);
    const [metadataError, setMetadataError] = useState(undefined);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        let active = true;
        const browserTarget = detectExtensionBrowserTargetFromNavigator(typeof window !== 'undefined' ? window.navigator : { userAgent: '' });
        const artifactPath = `/api/extension/artifacts/latest?browser=${encodeURIComponent(browserTarget)}`;
        console.info('[useExtensionVersionStatus] fetching latest extension artifact metadata', {
            browserTarget,
            artifactPath,
        });
        apiFetch(artifactPath, { skipAuthRedirect: true })
            .then((artifact) => {
            if (!active)
                return;
            setLatestArtifact(artifact);
            setMetadataError(undefined);
        })
            .catch((error) => {
            if (!active)
                return;
            if (error instanceof ApiError && error.status === 404) {
                setMetadataError('No extension artifact has been published yet.');
                return;
            }
            setMetadataError(error instanceof Error ? error.message : 'Failed to load extension artifact metadata.');
        });
        return () => {
            active = false;
        };
    }, [enabled]);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const updateInstalledVersion = (event) => {
            const detail = getDetail(event);
            if (detail.version) {
                setInstalledVersion(detail.version);
            }
            if (detail.buildSha) {
                setInstalledBuildSha(detail.buildSha);
            }
            setLastHeartbeatAt(detail.heartbeatAt ?? new Date().toISOString());
        };
        window.addEventListener(READY_EVENT, updateInstalledVersion);
        window.addEventListener(HEARTBEAT_EVENT, updateInstalledVersion);
        return () => {
            window.removeEventListener(READY_EVENT, updateInstalledVersion);
            window.removeEventListener(HEARTBEAT_EVENT, updateInstalledVersion);
        };
    }, [enabled]);
    const latestVersion = latestArtifact?.version;
    const updateAvailable = useMemo(() => isExtensionUpdateAvailable(installedVersion, latestVersion), [installedVersion, latestVersion]);
    return {
        installedVersion,
        installedBuildSha,
        latestArtifact,
        latestVersion,
        lastHeartbeatAt,
        updateAvailable,
        metadataError,
    };
}
